import React from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import QuickForm from '../components/forms/QuickForm';
import { 
  GraduationCap, Clock, Users, CheckCircle2,
  Target, Briefcase, TrendingUp, Phone
} from 'lucide-react';

export default function SSPage() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-violet-50 via-white to-purple-50/30" />
          <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-violet-100/40 rounded-full blur-3xl" />
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="flex flex-wrap gap-3 mb-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-50 border border-violet-100 rounded-full text-violet-700 text-sm font-medium">
                  <GraduationCap className="w-4 h-4" />
                  <span>Kurz pro střední školy</span>
                </div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-100 rounded-full text-emerald-700 text-sm font-medium">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Financovatelné z OP JAK II</span>
                </div>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight mb-4">
                AI pro střední školy
              </h1>
              
              <p className="text-xl md:text-2xl text-violet-700 font-medium mb-4">
                Příprava na budoucnost s umělou inteligencí
              </p>

              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Praktický kurz zaměřený na využití AI ve výuce, přípravě na maturitu a profesním rozvoji pedagogů.
              </p>

              <div className="flex flex-wrap gap-6 mb-8">
                <div className="flex items-center gap-2 text-slate-700">
                  <Clock className="w-5 h-5 text-violet-600" />
                  <span>8 vyučovacích hodin (rozděleno na 2×4 vyučovací hodiny, tj. 2×3 hodiny čistého času)</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <Users className="w-5 h-5 text-violet-600" />
                  <span>Prezenčně min. 8 / Online bez omezení</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <span className="text-2xl font-bold text-slate-900">3 400 Kč</span>
                  <span className="text-slate-500">/ účastník</span>
                </div>
              </div>
            </div>

            {/* Quick Form in Hero */}
            <div className="mt-12 max-w-xl">
              <div className="bg-white rounded-2xl p-6 shadow-xl shadow-violet-100/50 border border-violet-100">
                <div className="flex items-center gap-2 mb-4">
                  <Phone className="w-5 h-5 text-violet-600" />
                  <span className="font-semibold text-slate-900">Nechte nám kontakt, ozveme se</span>
                </div>
                <QuickForm courseType="ss" buttonText="Chci se dozvědět víc" variant="hero" />
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Co nabízí kurz AI pro SŠ
              </h2>
              <p className="text-lg text-slate-600">
                Komplexní přístup k využití AI v prostředí středních škol
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-8 border border-violet-100">
                <Target className="w-10 h-10 text-violet-600 mb-4" />
                <h3 className="text-xl font-bold text-slate-900 mb-3">Příprava výuky</h3>
                <p className="text-slate-600">
                  Efektivní tvorba výukových materiálů, testů a prezentací s pomocí AI nástrojů přizpůsobených středoškolské úrovni.
                </p>
              </div>

              <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-8 border border-violet-100">
                <Briefcase className="w-10 h-10 text-violet-600 mb-4" />
                <h3 className="text-xl font-bold text-slate-900 mb-3">Profesní orientace</h3>
                <p className="text-slate-600">
                  Využití AI pro přípravu studentů na trh práce, hodnocení kompetencí a kariérové poradenství.
                </p>
              </div>

              <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-8 border border-violet-100">
                <TrendingUp className="w-10 h-10 text-violet-600 mb-4" />
                <h3 className="text-xl font-bold text-slate-900 mb-3">Maturitní příprava</h3>
                <p className="text-slate-600">
                  Moderní metody přípravy na maturitní zkoušky s využitím AI pro individualizovanou podporu studentů.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Jak kurz probíhá
              </h2>
            </div>

            <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl p-8 md:p-12 border border-violet-100">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
                <div className="text-center">
                  <div className="w-14 h-14 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-7 h-7 text-violet-600" />
                  </div>
                  <p className="text-sm text-slate-500 mb-1">Rozsah</p>
                  <p className="font-semibold text-slate-900 text-sm">8 vyučovacích hodin (rozděleno na 2×4 vyučovací hodiny, tj. 2×3 hodiny čistého času)</p>
                </div>
                <div className="text-center">
                  <div className="w-14 h-14 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-4">
                    <GraduationCap className="w-7 h-7 text-violet-600" />
                  </div>
                  <p className="text-sm text-slate-500 mb-1">Forma</p>
                  <p className="font-semibold text-slate-900">prezenčně nebo online</p>
                </div>
                <div className="text-center">
                  <div className="w-14 h-14 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-7 h-7 text-violet-600" />
                  </div>
                  <p className="text-sm text-slate-500 mb-1">Min. účastníků</p>
                  <p className="font-semibold text-slate-900 text-sm">Prezenčně 8 / Online bez omezení</p>
                </div>
                <div className="text-center">
                  <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                  </div>
                  <p className="text-sm text-slate-500 mb-1">Financování</p>
                  <p className="font-semibold text-slate-900">OP JAK II / DVPP</p>
                </div>
              </div>

              <div className="text-center bg-white rounded-xl p-8 shadow-sm">
                <p className="text-slate-600 mb-2">Cena kurzu</p>
                <p className="text-4xl font-bold text-slate-900 mb-2">
                  3 400 Kč
                  <span className="text-lg font-normal text-slate-500"> / účastník</span>
                </p>
                <p className="text-sm text-slate-500 mb-4">
                  Platba probíhá standardně fakturou na vaši školu.
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  <span className="px-3 py-1 bg-violet-50 text-violet-700 rounded-full text-sm font-medium">
                    Financovatelné z OP JAK II
                  </span>
                  <span className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium">
                    Financovatelné z DVPP
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Big CTA Section */}
        <section className="py-20 bg-gradient-to-br from-violet-600 to-purple-700">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Máte zájem o kurz pro vaši školu?
              </h2>
              <p className="text-lg text-violet-100">
                Nechte nám kontakt a my se vám ozveme s termíny a dalšími informacemi.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 md:p-10 shadow-2xl">
              <QuickForm courseType="ss" buttonText="Chci se dozvědět víc" variant="hero" />
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
